import React, { useState, useRef } from 'react';
import { Card, Button, Form } from "tabler-react";
import { useForm } from '../../lib/useForm';
import PostObject from '../../lib/postObject';
import { toBase64 } from '../../lib/helpers';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';

const roles = ["SuperAdmin", "Administrator", "Operator"];

const AdminUserCreateModal = ({ closeMainModal, reload }) => {
  const { pathname } = useLocation();
  const [user, handleChange] = useForm({ firstName: '', lastName: '', email: '', password: '', phone: '' });
  const [selectedRole, setSelectedRole] = useState({ 
    label: pathname.split('admins/')[1] || roles[0], 
    value: pathname.split('admins/')[1] || roles[0] 
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const photoFile = useRef(null);

  const validateForm = () => {
    if (!user.firstName) {
      setError('Please enter first name');
      return false;
    }
    if (!user.lastName) {
      setError('Please enter last name');
      return false;
    }
    if (!user.email) {
      setError('Please enter email');
      return false;
    }
    if (!user.password) {
      setError('Please enter password');
      return false;
    }
    if (!user.phone) {
      setError('Please enter phone number');
      return false;
    }
    if (!selectedRole) {
      setError('Please select a role');
      return false;
    }
    if (photoFile.current.files.length === 0) {
      setError('Please select a photo');
      return false;
    }
    if (!photoFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file');
      return false;
    }
    return true;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setImagePreview(preview);
      setError('');
    } else if (file) {
      setError('Please select a valid image file');
      setImagePreview(null);
    }
  };

  const handleDropZoneClick = () => {
    photoFile.current.click();
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImagePreview(null);
    photoFile.current.value = '';
    // Force reset the file input
    if (photoFile.current) {
      photoFile.current.type = '';
      photoFile.current.type = 'file';
    }
  };

  const submitForm = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);
      
      const adminUser = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: user.password,
        phone: user.phone,
        photo: await toBase64(photoFile.current.files[0]),
        roles: [selectedRole.value]
      };
      
      await PostObject('admins', adminUser);
      
      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to create admin user. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>Create Admin User</Card.Title>
        <Card.Options>
          <Button
            size="sm"
            icon="x"
            onClick={closeMainModal}
          />
        </Card.Options>
      </Card.Header>
      <Card.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
          <Form.FieldSet>
            <Form.Input
              name='firstName'
              label='First Name'
              placeholder='Enter first name'
              value={user['firstName']}
              onChange={handleChange}
              required
            />

            <Form.Input
              name='lastName'
              label='Last Name'
              placeholder='Enter last name'
              value={user['lastName']}
              onChange={handleChange}
              required
            />

            <Form.Input
              name='email'
              label='E-mail'
              placeholder='Enter email address'
              type="email"
              value={user['email']}
              onChange={handleChange}
              required
            />

            <Form.Input
              name='password'
              label='Password'
              placeholder='Enter password'
              type="password"
              value={user['password']}
              onChange={handleChange}
              required
            />

            <Form.Input
              name='phone'
              label='Phone'
              placeholder='Enter phone number'
              type="tel"
              value={user['phone']}
              onChange={handleChange}
              required
            />

            <Form.Label>Role *</Form.Label>
            <Select
              value={selectedRole}
              onChange={setSelectedRole}
              options={roles.map(role => {
                return ({ label: role, value: role });
              })}
              placeholder="Select a role"
              isClearable
            />

            <Form.Group>
              <Form.Label>Photo *</Form.Label>
              <input 
                type='file' 
                ref={photoFile}
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              
              {!imagePreview ? (
                <div className="drop-zone" onClick={handleDropZoneClick}>
                  <div className="drop-zone-icon">
                    <svg 
                      width="48" 
                      height="48" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#6c757d" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21,15 16,10 5,21"/>
                    </svg>
                  </div>
                  <div className="drop-zone-text">Click here to upload admin photo</div>
                  <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                </div>
              ) : (
                <div className="image-preview-container">
                  <img 
                    src={imagePreview} 
                    alt="Admin Preview" 
                    className="image-preview"
                    onClick={handleDropZoneClick}
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveImage}
                  >
                    ×
                  </button>
                </div>
              )}
            </Form.Group>
          </Form.FieldSet>
        </Form>
      </Card.Body>
      <Card.Footer>
        <div className="modal-footer">
          <Button
            color="primary"
            size="sm"
            className={`ms-2 ${saveLoading ? 'btn-loading' : ''}`}
            onClick={submitForm}
            disabled={saveLoading}
          >
            Save
          </Button>

          <Button
            color="secondary"
            size="sm"
            className="ms-2"
            onClick={closeMainModal}
            disabled={saveLoading}
          >
            Cancel
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
};

export default AdminUserCreateModal;